import React, { useState } from "react";
import FieldBuilder from "../components/FieldBuilder";
import { Section } from "../types/Section";
import DynamicForm from "../components/DynamicForm";
import { DynamicField } from "../types/DynamicField";
import "../styles/PageFormGenerator.css";
import AuthentifiedPage from "./AuthentifiedPage";
import { useNavigate ,useLocation } from "react-router-dom";
import CollecteSuccessMessage from "../components/CollecteSuccessMessage";
import { buildFormulairePayload, FormulaireService } from "../services/FormulaireService";
import { FormulairePayload } from "../types/FormulairePayload";
import { useAuth } from "../context/AuthContext";

const PageFormGenerator = () => {
  const [fields, setFields] = useState<DynamicField[]>([]);
  const [title, setTitle] = useState<string>("");
  const [sections, setSections] = useState<Section[]>([]);
  const [currentSection, setCurrentSection] = useState<string>("");
  const [successVisible, setSuccessVisible] = useState(false);
  const {api}=useAuth();
  const navigate= useNavigate();
  
  const handleAddField = (field: DynamicField) => {
    setFields((prev) => [...prev, field]);
  };

  const handleAddSection = (name: string): Section | undefined => {
    const n = name.trim();
    if (!n) return undefined;
    const existing = sections.find((s) => s.name === n);
    if (existing) {
      setCurrentSection(existing.id);
      return existing;
    }
    const sec: Section = { id: (typeof crypto !== "undefined" && (crypto as any).randomUUID) ? (crypto as any).randomUUID() : Date.now().toString(), name: n };
    setSections((s) => [...s, sec]);
    setCurrentSection(sec.id);
    return sec;
  };

  const saveFormToLocalStorage = (domainId:number, sousDomainId:number, sousSousDomainId:number, title:string, sections:Section[], fields:DynamicField[]) => {
    const key = "formulaires";

    const stored = JSON.parse(localStorage.getItem(key) || "[]");

    const form = {
      id: Date.now(),
      domainId,
      sousDomainId,
      sousSousDomainId,
      title,
      sections,
      fields
    };

    stored.push(form);

    localStorage.setItem(key, JSON.stringify(stored));
  };

  const handleRenameSection = (sec: Section) => {
    const newName = prompt("Renommer la section", sec.name);
    if (!newName) return;
    setSections((s) => s.map((x) => (x.id === sec.id ? { ...x, name: newName } : x)));
    setFields((f) => f.map((fld) => (fld.section && fld.section.id === sec.id ? { ...fld, section: { ...fld.section, name: newName } } : fld)));
  };

  const handleDeleteSection = (sec: Section) => {
    if (!window.confirm(`Supprimer la section "${sec.name}" ? (les champs resteront sans section)`)) return;
    setSections((s) => s.filter((x) => x.id !== sec.id));
    setFields((f) => f.filter((fld) => !(fld.section && fld.section.id === sec.id)));
    if (currentSection === sec.id) setCurrentSection("");
  };

  const handleDeleteField = (fieldId: string) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce champ ?")) return;
    
    setFields((prev) => prev.filter((f) => {
      if (f.id === fieldId) return false;      
      if (f.conditionalLogic && f.conditionalLogic.fieldId === fieldId) return false;      
      return true;
    }));
  };

  const handleClear = () => {
    setFields([]);
    setTitle("");
  };

  const location = useLocation();
  const { domain, sousDomain, sousSousDomain } = location.state || {};

  const handleCreateForm = async () => {
    if (!title.trim()) {
      alert("Veuillez entrer un titre pour le formulaire.");
      return;
    }
    if (fields.length === 0) {
      alert("Veuillez ajouter au moins un champ.");
      return;
    }

    let sousDomainId=sousDomain.id;
    let sousSousDomainId=0;
    if(sousSousDomain!=undefined){
      sousDomainId=0;
      sousSousDomainId=sousSousDomain.id;
    }

    const payload:FormulairePayload=buildFormulairePayload(title,sections,fields,sousDomainId,sousSousDomainId);

    try{
      await FormulaireService.saveFormulaire(api,sousDomainId,sousSousDomainId,payload);
      alert("Insertion réussie");
      navigate("/agent");
      
    }catch(e){
      alert("Erreur lors de l'insertion : "+e);
    }
    saveFormToLocalStorage(domain.id, sousDomainId, sousSousDomainId, title, sections, fields);
    handleClear();
    setSections([]);
    setCurrentSection("");
  };

  return (
    <AuthentifiedPage>
      <CollecteSuccessMessage
        visible={successVisible}
        message="Utilisateur enregistré avec succès"
      />
      <div className="page-form-generator container py-4">
        <div className="row gy-4">
          <div className="col-12 col-lg-5">
            <div className="card shadow-sm p-3">
              <h2 className="mb-3">Création de formulaire</h2>
              <div className="mb-3">
                <label className="form-label">Titre du formulaire</label>
                <input
                  className="form-control"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Titre du formulaire"
                />
              </div>

              <FieldBuilder
                onAddField={handleAddField}
                sections={sections}
                currentSection={sections.find((s) => s.id === currentSection)}
                onCreateSection={handleAddSection}
                onSelectSection={(id: string) => setCurrentSection(id)}
                existingFields={fields}
              />

              <div className="d-flex flex-wrap gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 w-lg-auto"
                  onClick={handleClear}
                  style={{ borderRadius: "25px" }}
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-7">
            <div className="card shadow-sm">
              <div className="card-body">
                <h2 className="card-title">Aperçu du formulaire</h2>

                <h5 className="domain-info">
                  Domaine : {domain.label}
                  {sousDomain && (
                    <span className="sub-domain">Fenêtre : {sousDomain.label}</span>
                  )}
                  {sousSousDomain && (
                    <span className="sub-domain"> Sous-fenêtre : {sousSousDomain.label}</span>
                  )}
                </h5>

                {title && <h5 className="form-title-preview mb-3">{title}</h5>}
                {sections.length > 0 && (
                  <div className="mb-3">
                    <small className="text-muted d-block">Sections :</small>

                    <div className="section-list mt-2">
                      {sections.map((sec) => (
                        <div key={sec.id} className="section-item">
                          <button
                            type="button"
                            className={`section-button ${
                              currentSection === sec.id ? "active" : ""
                            }`}
                            onClick={() => setCurrentSection(sec.id)}
                          >
                            {sec.name}
                          </button>

                          <button
                            type="button"
                            className="section-action delete text-success"
                            onClick={() => handleDeleteSection(sec)}
                            title="Supprimer"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AJOUT: Liste des champs ajoutés avec possibilité de suppression */}
                {fields.length > 0 && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-2">Champs ajoutés :</small>
                    <div className="list-group">
                      {fields.map((field) => (
                        <div 
                          key={field.id} 
                          className="list-group-item d-flex justify-content-between align-items-center py-2"
                        >
                          <div className="flex-grow-1">
                            <strong>{field.label}</strong>
                            <small className="text-muted ms-2">
                              ({field.type.label})
                            </small>
                            {field.section && (
                              <small className="text-muted ms-2">
                                - {field.section.name}
                              </small>
                            )}
                            {field.conditionalLogic && (
                              <span className="badge bg-info ms-2" style={{ fontSize: '0.7rem' }}>
                                Conditionnel
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteField(field.id)}
                            title="Supprimer ce champ"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <hr className="my-3" />

                <h5 className="mb-3">Prévisualisation</h5>
                {fields.length === 0 ? (
                  <div className="text-muted small">Aucun champ ajouté</div>
                ) : (
                  <DynamicForm fields={fields} />
                )}
              </div>

              <div className="card-footer bg-white border-top">
                <button
                  type="button"
                  className="btn btn-form-create w-49 w-lg-auto"
                  onClick={handleCreateForm}
                >
                  Créer le formulaire
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthentifiedPage>
  );
};

export default PageFormGenerator;